import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	useMutation,
	UseMutationResult,
	useQuery,
	useQueryClient,
} from 'react-query';
import cloneDeep from 'lodash.clonedeep';
import shortUUID from 'short-uuid';
import { splitActionKey } from '@shared/utils/splitActionKey';
import {
	CargoContractSides,
	CargoContractStatusTypes,
	CargoContractTypes,
	CargoDeclaredTypes,
	CargoTermTypes,
	CargoUnitTypes,
	Currencies,
	DemurrageTerms,
	EstimateStatus,
	FreightRateType,
	LaytimeCalculationMethods,
	LaytimeIntervals,
	LaytimeSettlementTypes,
	LaytimeTerms,
	PortActionTypes,
	QuantityOptions,
	ReletStatus,
} from '@shared/utils/constants';
import { getCargoPortLaytimeEstimatedValues } from '@shared/utils/getCargoPortLaytimeEstimatedValues';
import {
	convertDaysToInterval,
	convertIntervalToDays,
} from '@shared/utils/laytime';
import {
	isDischargePort,
	isLoadPort,
} from '@shared/utils/cargoPortHelpers';
import { getCargoPortKey } from '@shared/utils/getCargoPortKey';
import type { CargoProps } from '@api/models/cargo';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { GetCargosResponse } from '@api/features/cargos/getCargos';
import type {
	CargoDetails,
	GetCargoDetailsResponse,
} from '@api/features/cargos/getCargoDetails';
import type { EditableCargo, UpdateCargoRequest } from '@api/features/cargos/updateCargo';
import type { CargoPortProps } from '@api/models/cargo-port';
import type { CargoPortWithEstimatedValues } from '@api/features/cargos/transformCargos';
import type { Port } from '@api/utils/ports';
import {
	addCargoToEstimate,
	createCargo,
	createCargoRelet,
	deleteCargo,
	deleteCargoRelet,
	getCargoDetails,
	getPorts,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import { isSyncedCargoKeyField, isSyncedCargoPortField } from '@client/screens/cargos/helpers/cargoUtils';
import { useAuth } from '@client/lib/auth';

export type ReturnResult<Fn extends (...args: any) => any> = Awaited<ReturnType<Fn>>;

export type OnUpdateCargo = (
	cargoId: number,
	attributes: UpdateCargoRequest['attributes'],
) => void;

export type CargoChanges = { cargoId: number; attributes: Partial<UpdateCargoRequest['attributes']> };
export type CargoPortChanges = {
	cargoId: number;
	cargoPortId: number;
	attributes: Partial<CargoPortProps>;
};

export type OnImportCargoMutator = UseMutationResult<
	ReturnResult<typeof addCargoToEstimate>,
	Error,
	number[]
>;

export type OnCreateNewCargoMutator = UseMutationResult<
	ReturnResult<typeof createCargo>,
	Error,
	void
>;

export type OnCreateNewCargoReletMutator = UseMutationResult<
	ReturnResult<typeof createCargoRelet>,
	Error,
	void
>;

export type OnDeleteCargoMutator = UseMutationResult<
	ReturnResult<typeof deleteCargo>,
	Error,
	number
>;

export type OnDeleteCargoReletMutator = UseMutationResult<
	ReturnResult<typeof deleteCargoRelet>,
	Error,
	number
>;

export type UpdateCargoPort = {
	cargoId: number;
	cargoPortId: number;
	attributes: CargoPortProps & { estimatedTimeAllowed?: number | null };
};

const getEmptyCargoPort = (
	quantity: number | null,
	portId: number,
	action: 'loading' | 'discharging',
	ports: Array<Port>
) => {
	const port = ports.find((p) => p.id === portId);

	if (!port) {
		throw new Error('Port not found!');
	}

	const cp: CargoPortWithEstimatedValues = {
		blDate: null,
		blQuantity: null,
		calculationMethod: LaytimeCalculationMethods.SHINC,
		cargoId: -1,
		cpQuantity: quantity ?? null,
		createdAt: new Date(),
		deletedAt: null,
		demurrage: null,
		despatch: null,
		estimatedTimeAllowed: null,
		estimatedTimeUsed: null,
		// @ts-ignore it's ok its only temporary
		id: shortUUID.uuid(),
		idleDays: null,
		includeDeadfreight: false,
		laycanFrom: null,
		laycanTo: null,
		laytimeFrom: null,
		laytimeInterval: LaytimeIntervals.DAY,
		laytimeQuantityOverride: null,
		laytimeTo: null,
		linerCostCurrency: Currencies.USD,
		linerCostExchangeRate: 1,
		linerCostPricePerUnit: null,
		linerCostUnit: CargoUnitTypes.MT,
		loadingRate: 0,
		port,
		portAndActionKey: getCargoPortKey(port.id, 0, action),
		portCallId: null,
		secondaryBlQuantity: null,
		secondaryCpQuantity: null,
		stsOpsMotherVessel: null,
		updatedAt: new Date(),
		useStsOps: false,
	};

	return cp;
};

const despatchUsesHalfDemurrage = (cargo: { despatchHalfDemurrage?: boolean | null }) => (
	cargo.despatchHalfDemurrage !== false
);

type Props = {
	creatingCargoOnContract?: boolean;
	cargoId?: number | null;
	estimate?: GetEstimateDetailsResponse | null | undefined;
	selectedEstimateId: number | null;
	centralCargos?: CargoDetails[];
	syncCargos?: boolean;
	setSyncCargos?: React.Dispatch<React.SetStateAction<boolean>>;
	refreshEverything?: () => Promise<void>;
	setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
	handleCargoIdChange?: (id: number) => void;
};

/*
* Handlers for cargos. If cargoId is specified, it's being used on CargoDetailsScreen,
* if estimateId is specified its on the estimate
*/
export const useCargoHandlers = ({
	creatingCargoOnContract,
	cargoId,
	estimate,
	centralCargos,
	syncCargos,
	setSyncCargos,
	refreshEverything,
	selectedEstimateId,
	setLoading,
}: Props) => {
	const inEstimate = estimate != null;
	const queryClient = useQueryClient();
	const [reletViewByEstimateId, setReletViewByEstimateId] = useState<Record<number, boolean>>({});
	const { userInfo } = useAuth();

	const getEmptyCargo = useCallback((): CargoDetails => {
		const c = {
			addressCommission: 0,
			blNumber: '',
			brokerCommission: 0,
			charterer: null,
			fixingCompanyId: userInfo.fixingCompanies.find((c) => c.isDefault)?.id ?? null,
			cpDate: null,
			createdAt: new Date(),
			currency: Currencies.USD,
			deadfreightAmount: null,
			deletedAt: null,
			demurrage: null,
			demurrageDespatchInterval: LaytimeIntervals.DAY,
			demurrageDischarge: null,
			demurrageLoading: null,
			demurrageTerm: DemurrageTerms.DEMURRAGE,
			despatch: null,
			despatchDischarge: null,
			despatchLoading: null,
			despatchHalfDemurrage: userInfo.defaultEstimatorDespatchHalfDemurrage !== false,
			dischargePorts: [],
			estimatedItems: [],
			estimatedTimeUsed: null,
			estimatedTimeUsedDischarging: null,
			estimatedTimeUsedLoading: null,
			exchangeRate: 1,
			fixtureId: -1,
			freightRate: 0,
			freightType: userInfo.defaultEstimatorFreightType ?? FreightRateType.PER_UNIT,
			ghost: false,
			id: -1,
			laytimeTerms: userInfo.defaultEstimatorLaytimeTerms ?? LaytimeTerms.NON_REVERSIBLE,
			loadingPorts: [],
			organizationId: -1,
			overageAmount: null,
			quantity: 0,
			quantityOption: QuantityOptions.MOLOO,
			quantityTolerance: null,
			secondaryUnit: CargoUnitTypes.MT,
			status: EstimateStatus.Available,
			stowageFactor: 0,
			terms: CargoTermTypes.FIO,
			timeAllowed: null,
			timeAllowedDischarging: null,
			timeAllowedInterval: LaytimeIntervals.DAY,
			timeAllowedLoading: null,
			updatedAt: new Date(),
			brokers: [],
			cargoPorts: {},
			CargoPorts: [],
			type: 'New cargo',
			unit: CargoUnitTypes.MT,
			voyageId: -1,
			chartererName: '',
			estimateCargoId: null,
			isCompleted: false,
			firstLaycan: null,
			lastLaycan: null,
			contractStatus: CargoContractStatusTypes.Pending,
			declared: CargoDeclaredTypes.Declared,
			contractType: CargoContractTypes.SingleCargo,
			partCargo: null,
			holdingReportGroups: null,
			nominatedVesselId: null,
			fixedBy: null,
			tcePerDay: null,
			tcePerDayCurrency: Currencies.USD,
			tcePerDayExchangeRate: 1,
			totalDays: null,
			contractSide: CargoContractSides.VC_OUT,
			reletCargoId: null,
			hasRelet: 'No' as ReletStatus,
			description: null,
			settlementType: LaytimeSettlementTypes.DEMURRAGE,
			settlementAmount: null,
			settlementTypeLoading: LaytimeSettlementTypes.DEMURRAGE,
			settlementTypeDischarging: LaytimeSettlementTypes.DEMURRAGE,
			settlementAmountLoading: null,
			settlementAmountDischarging: null,
		};

		return c;
	}, [
		userInfo.defaultEstimatorFreightType,
		userInfo.defaultEstimatorLaytimeTerms,
		userInfo.defaultEstimatorDespatchHalfDemurrage,
		userInfo.fixingCompanies,
	]);

	const [localCargoData, setLocalCargoData] = useState<Array<CargoDetails> | null>(() => (
		creatingCargoOnContract ? [getEmptyCargo()] : null
	));
	const [
		localCargoPortData,
		setLocalCargoPortData,
	] = useState<Array<CargoPortWithEstimatedValues> | null | undefined>(null);
	const [pendingCargoChanges, setPendingCargoChanges] = useState<Array<CargoChanges>>([]);
	const [
		pendingCargoPortChanges,
		setPendingCargoPortChanges,
	] = useState<Array<CargoPortChanges>>([]);

	const {
		data: ports,
	} = useQuery('ports', getPorts);

	const hardRefresh = async (incomingId?: number | null) => {
		const idToUse = incomingId ?? cargoId;

		if (idToUse == null || idToUse <= 0) {
			return;
		}

		queryClient.invalidateQueries(['cargos']);
		queryClient.invalidateQueries(['allCargos']);
		queryClient.invalidateQueries(['fixtureDetails']);
		queryClient.invalidateQueries(['voyageDetails']);

		const cargoDetails = await getCargoDetails(idToUse);
		let cargosToSet = [cargoDetails];

		if (cargoDetails.reletCargoId != null) {
			const otherCargoDetails = await getCargoDetails(cargoDetails.reletCargoId);
			cargosToSet = cargoDetails.contractSide === CargoContractSides.VC_IN ?
				[otherCargoDetails, cargoDetails] :
				[cargoDetails, otherCargoDetails];
		}

		setLocalCargoData(cargosToSet);
		setLocalCargoPortData(cargosToSet.flatMap((c) => Object.values(c.cargoPorts ?? {})));
	};

	const [
		_cargo,
		_refresh,
		_error,
		loading
	] = useFetchedState(async () => {
		if (cargoId != null) {
			const cargoDetails = await getCargoDetails(cargoId);

			let cargosToSet = [cargoDetails];

			const otherReletCargoId =
				cargoDetails.contractSide === CargoContractSides.VC_IN ?
					cargoDetails.reletCargoId :
					cargoDetails.reletCargoId;

			if (otherReletCargoId != null) {
				const otherCargoDetails = await getCargoDetails(otherReletCargoId);
				cargosToSet = cargoDetails.contractSide === CargoContractSides.VC_IN ?
					[otherCargoDetails, cargoDetails] :
					[cargoDetails, otherCargoDetails];
			}

			setLocalCargoData(cargosToSet);

			const cargoPorts = cargosToSet.flatMap((c) => Object.values(c.cargoPorts ?? {}));

			setLocalCargoPortData(cargoPorts);
		}

		return null;
	}, [cargoId]);

	useEffect(() => {
		if (creatingCargoOnContract) {
			return;
		}

		if (
			((localCargoData == null && centralCargos != null) ||
				(localCargoData?.length !== centralCargos?.length) ||
				syncCargos) && (cargoId == null)
		) {
			setLocalCargoData(centralCargos ?? []);

			const cargoPorts = centralCargos?.map((c) => c.cargoPorts).flat();
			const transformed = cargoPorts?.map((c) => Object.values(c));
			setLocalCargoPortData(transformed?.flat());
			setSyncCargos?.(false);
		}
	}, [creatingCargoOnContract, cargoId, centralCargos, estimate, inEstimate, localCargoData, setSyncCargos, syncCargos]);

	const getSelectedEstimateId = () => {
		if (selectedEstimateId == null) {
			throw new Error('selectedEstimateId is required');
		}

		return selectedEstimateId;
	};

	const onImportCargoMutator: OnImportCargoMutator = useMutation(
		{
			mutationFn: (ids: number[]) => addCargoToEstimate(getSelectedEstimateId(), ids),
			onMutate: () => setLoading?.(true),
			onSuccess: async () => {
				refreshEverything?.();
				setLoading?.(false);
			},
		},
	);

	const onCreateNewCargoMutator: OnCreateNewCargoMutator = useMutation(
		{
			mutationFn: () => createCargo({
				estimateId: getSelectedEstimateId(),
				attributes: {
					type: 'new cargo',
				}
			}),
			onMutate: () => setLoading?.(true),
			onSuccess: async () => {
				refreshEverything?.();
				setLoading?.(false);
			},
		},
	);

	const onCreateNewCargoReletMutator: OnCreateNewCargoReletMutator = useMutation(
		{
			mutationFn: () => createCargoRelet({
				estimateId: getSelectedEstimateId(),
			}),
			onMutate: () => setLoading?.(true),
			onSuccess: async () => {
				refreshEverything?.();
			},
			onError: (e) => {
				showErrorNotification('Failed to create cargo relet', e as Error);
			},
			onSettled: () => {
				setLoading?.(false);
			},
		},
	);

	const onDeleteCargoMutator: OnDeleteCargoMutator = useMutation(
		{
			mutationFn: (id: number) => {
				if (localCargoData?.[0]?.voyageId) {
					return deleteCargo(id);
				}

				return deleteCargo(id, getSelectedEstimateId(), false);
			},
			onMutate: () => setLoading?.(true),
			onSuccess: async () => {
				if (selectedEstimateId != null) {
					refreshEverything?.();
				}

				setLoading?.(false);
				showSuccessNotification('Cargo removed');
			},
			onError: (e) => {
				showErrorNotification('Failed to remove cargo', e as Error);
			},
		},
	);

	const onDeleteCargoReletMutator: OnDeleteCargoReletMutator = useMutation(
		{
			mutationFn: (id: number) => {
				if (localCargoData?.[0]?.voyageId) {
					return deleteCargoRelet(id);
				}

				return deleteCargoRelet(id, getSelectedEstimateId(), false);
			},
			onMutate: () => setLoading?.(true),
			onSuccess: async () => {
				if (selectedEstimateId != null) {
					refreshEverything?.();
				}

				setLoading?.(false);
				showSuccessNotification('Cargo removed');
			},
			onError: (e) => {
				showErrorNotification('Failed to remove cargo', e as Error);
			},
		},
	);

	const onUpdateCargo: OnUpdateCargo = async (id, attributes) => {
		const relevantCargo = localCargoData?.find((c) => c.id === id);

		if (relevantCargo == null) {
			return;
		}

		let cargosToValidate = estimate?.cargos;
		const cargoToUpdate = cargosToValidate?.find(((c) => c.id === id));
		const updatedCargoEntry = {
			...cargoToUpdate,
			...attributes,
		};

		cargosToValidate = cargosToValidate?.filter((x) => x.id !== id);
		cargosToValidate?.push(updatedCargoEntry as CargoDetails);

		setPendingCargoChanges((prev) => {
			const existingPendingChange = prev.find((c) => c.cargoId === id);
			let attributesToUpdate = attributes;

			if (existingPendingChange != null) {
				attributesToUpdate = {
					...existingPendingChange.attributes,
					...attributes,
				};
			}

			const {
				demurrage,
				demurrageLoading,
				demurrageDischarge,
				timeAllowed,
				timeAllowedLoading,
				timeAllowedDischarging,
				estimatedTimeUsed,
			} = attributesToUpdate;

			const cargoForDhd = {
				...relevantCargo,
				...attributesToUpdate,
			};

			if (demurrage != null && despatchUsesHalfDemurrage(cargoForDhd)) {
				attributesToUpdate.despatch = demurrage / 2;
			}

			if (demurrageLoading != null && despatchUsesHalfDemurrage(cargoForDhd)) {
				attributesToUpdate.despatchLoading = demurrageLoading / 2;
			}

			if (demurrageDischarge != null && despatchUsesHalfDemurrage(cargoForDhd)) {
				attributesToUpdate.despatchDischarge = demurrageDischarge / 2;
			}

			if (timeAllowed && !estimatedTimeUsed) {
				attributesToUpdate.estimatedTimeUsed = timeAllowed;
			}

			if (timeAllowedLoading && !attributes.estimatedTimeUsedLoading) {
				attributesToUpdate.estimatedTimeUsedLoading = timeAllowedLoading;
			}

			if (timeAllowedDischarging && !attributes.estimatedTimeUsedDischarging) {
				attributesToUpdate.estimatedTimeUsedDischarging = timeAllowedDischarging;
			}

			const otherCargos = prev.filter((c) => c.cargoId !== id);
			const updatedArr = [
				...otherCargos,
				{
					cargoId: id,
					attributes: attributesToUpdate,
				},
			];

			return updatedArr;
		});

		// Structured clone fails in some instances due to there being functions in the moment object 
		// Issue goes away if we serialize first
		let clonedLocalCargoPorts = JSON.parse(structuredClone(JSON.stringify(localCargoPortData))) as CargoPortWithEstimatedValues[] ?? [];
		const cargoPortsToUpdate: CargoPortWithEstimatedValues[] = [];

		if ((attributes.loadingPorts || attributes.dischargePorts) && ports && creatingCargoOnContract) {
			let existingLoadPorts = clonedLocalCargoPorts.filter(isLoadPort);
			let existingDischargePorts = clonedLocalCargoPorts.filter(isDischargePort);

			if (attributes.loadingPorts) {
				existingLoadPorts = existingLoadPorts.filter((lp) => {
					return (attributes.loadingPorts as number[])?.includes(lp.port.id);
				});
			}

			if (attributes.dischargePorts) {
				existingDischargePorts = existingDischargePorts.filter((dp) => {
					return (attributes.dischargePorts as number[])?.includes(dp.port.id);
				});
			}

			const loadPorts = (attributes.loadingPorts ?? []).map((lpId) => getEmptyCargoPort(
				localCargoData?.[0].quantity ?? null,
				lpId as number,
				'loading',
				ports
			));

			const dischargePorts = (attributes.dischargePorts ?? []).map((dpId) => getEmptyCargoPort(
				localCargoData?.[0].quantity ?? null,
				dpId as number,
				'discharging',
				ports
			));

			const loadPortsToSet: CargoPortWithEstimatedValues[] = [];
			const dischargePortsToSet: CargoPortWithEstimatedValues[] = [];

			loadPorts.forEach((cp) => {
				const exists = clonedLocalCargoPorts.find((p) => {
					const { action } = splitActionKey(p.portAndActionKey);

					if (action === PortActionTypes.LOADING && p.port.id === cp.port.id) {
						return true;
					}

					return false;
				});

				if (!exists) {
					loadPortsToSet.push(cp);
				}
			});

			dischargePorts.forEach((cp) => {
				const exists = clonedLocalCargoPorts.find((p) => {
					const { action } = splitActionKey(p.portAndActionKey);

					if (action === PortActionTypes.DISCHARGING && p.port.id === cp.port.id) {
						return true;
					}

					return false;
				});

				if (!exists) {
					dischargePortsToSet.push(cp);
				}
			});

			clonedLocalCargoPorts = [
				...existingLoadPorts,
				...existingDischargePorts,
				...loadPortsToSet,
				...dischargePortsToSet,
			];
		}

		const cargoPorts = clonedLocalCargoPorts?.reduce((acc, cp) => {
			const {
				totalTimeInPort,
				estimatedDemurrage,
				estimatedDespatch,
				estimatedTimeAllowed,
				idleDays,
				workingDays,
				demurrage,
				despatch,
			} = getCargoPortLaytimeEstimatedValues({
				cargo: relevantCargo as CargoProps,
				incomingCargoAttributes: attributes,
				cargoPort: cp,
				cargoPorts: clonedLocalCargoPorts,
			});

			const timeAllowedInLaytimeInterval = convertDaysToInterval(estimatedTimeAllowed, cp.laytimeInterval);

			const values: CargoPortWithEstimatedValues = {
				...cp,
				totalTimeInPort: totalTimeInPort ?? 0,
				estimatedDemurrage: Math.abs(estimatedDemurrage ?? 0),
				estimatedDespatch: estimatedDespatch ?? 0,
				estimatedTimeAllowed: timeAllowedInLaytimeInterval,
				estimatedTimeUsed: attributes.estimatedTimeUsed != null ?
					convertDaysToInterval(
						convertIntervalToDays(
							attributes.estimatedTimeUsed,
							attributes.timeAllowedInterval ?? relevantCargo.timeAllowedInterval),
						cp.laytimeInterval,
					) :
					timeAllowedInLaytimeInterval,
				idleDays,
				workingDays,
				demurrage,
				despatch,
			};

			cargoPortsToUpdate.push(values);

			return {
				...acc,
				[cp.portAndActionKey]: values,
			};
		}, {});

		setLocalCargoData((prev) => {
			if (prev == null) {
				return [];
			}

			const {
				demurrage,
				demurrageLoading,
				demurrageDischarge,
				timeAllowed,
				timeAllowedLoading,
				timeAllowedDischarging,
				laytimeTerms,
			} = attributes;

			if (laytimeTerms) {
				attributes.timeAllowed = null;
				attributes.estimatedTimeUsed = null;
				attributes.timeAllowedLoading = null;
				attributes.timeAllowedDischarging = null;
				attributes.despatchLoading = null;
				attributes.demurrageLoading = null;
				attributes.demurrageDischarge = null;
				attributes.despatchDischarge = null;
				attributes.estimatedTimeUsedLoading = null;
				attributes.estimatedTimeUsedDischarging = null;
				attributes.despatch = null;
				attributes.demurrage = null;
			}

			const cargoForDhd = {
				...relevantCargo,
				...attributes,
			};

			if (demurrage != null && despatchUsesHalfDemurrage(cargoForDhd)) {
				attributes.despatch = demurrage / 2;
			}

			if (demurrageLoading != null && despatchUsesHalfDemurrage(cargoForDhd)) {
				attributes.despatchLoading = demurrageLoading / 2;
			}

			if (demurrageDischarge != null && despatchUsesHalfDemurrage(cargoForDhd)) {
				attributes.despatchDischarge = demurrageDischarge / 2;
			}

			if (timeAllowed) {
				attributes.estimatedTimeUsed = timeAllowed;
			}

			if (timeAllowedLoading) {
				attributes.estimatedTimeUsedLoading = timeAllowedLoading;
			}

			if (timeAllowedDischarging) {
				attributes.estimatedTimeUsedDischarging = timeAllowedDischarging;
			}

			const updatedCargo = {
				...relevantCargo,
				...attributes,
				loadingPorts: attributes.loadingPorts ?? relevantCargo.loadingPorts ?? [],
				dischargePorts: attributes.dischargePorts ?? relevantCargo.dischargePorts ?? [],
				cargoPorts,
				key: relevantCargo.id,
			};

			const syncedAttributes = Object.fromEntries(
				Object.entries(attributes).filter(([key]) => (
					isSyncedCargoKeyField(key as keyof EditableCargo)
				)),
			) as Partial<UpdateCargoRequest['attributes']>;

			return prev.map((c) => {
				if (c.id === relevantCargo.id) {
					return updatedCargo;
				}

				if (
					relevantCargo.contractSide === CargoContractSides.VC_OUT &&
					c.contractSide === CargoContractSides.VC_IN &&
					(
						c.reletCargoId === relevantCargo.id ||
						relevantCargo.reletCargoId === c.id
					) &&
					Object.keys(syncedAttributes).length > 0
				) {
					return {
						...c,
						...syncedAttributes,
						loadingPorts: syncedAttributes.loadingPorts != null ?
							relevantCargo.loadingPorts.filter((p) =>
								(syncedAttributes.loadingPorts as number[]).includes(p.id),
							) :
							c.loadingPorts,
						dischargePorts: syncedAttributes.dischargePorts != null ?
							relevantCargo.dischargePorts.filter((p) =>
								(syncedAttributes.dischargePorts as number[]).includes(p.id),
							) :
							c.dischargePorts,
						key: c.id,
					};
				}

				return c;
			}) as Array<GetCargoDetailsResponse> | GetCargosResponse['rows'];
		});
		if (creatingCargoOnContract) {
			setLocalCargoPortData(cargoPortsToUpdate);
		} else {
			setLocalCargoPortData((prev) => prev?.map((cp) => {
				const updated = cargoPortsToUpdate.find((c) => c.id === cp.id);

				return updated ?? cp;
			}));
		}
	};

	const onUpdateCargoPort = (
		values: UpdateCargoPort,
		isCopy?: boolean,
		isSyncedUpdate?: boolean,
	) => {
		const toUpdate = {
			cargoId: values.cargoId,
			cargoPortId: values.cargoPortId,
			attributes: values.attributes,
		};

		const relevantCargo = localCargoData?.find((c) => c.id === values.cargoId);
		const relevantCargoPort = localCargoPortData?.find((cp) => cp.id === values.cargoPortId);

		if (relevantCargo == null || relevantCargoPort == null) {
			return;
		}

		setPendingCargoPortChanges((prev) => {
			const existing = prev.find((p) => p.cargoPortId === values.cargoPortId);

			if (existing == null) {
				return [...prev, toUpdate];
			}

			return prev.map((p) => (
				p.cargoPortId === values.cargoPortId ?
					{
						...toUpdate,
						attributes: {
							...p.attributes,
							...toUpdate.attributes,
						},
					} :
					p
			));
		});

		// @ts-ignore
		setLocalCargoPortData((prev) => {
			if (prev == null) {
				return [];
			}

			const cargoPort = prev.find((cp) => cp.id === values.cargoPortId);

			const updatedCargoPort = {
				...cargoPort,
				...values.attributes,
			};

			if (
				relevantCargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE &&
				values.attributes.demurrage != null &&
				despatchUsesHalfDemurrage(relevantCargo)
			) {
				values.attributes.despatch = values.attributes.demurrage / 2;
			}

			const calculatedValues = getCargoPortLaytimeEstimatedValues({
				cargo: relevantCargo,
				cargoPort: updatedCargoPort,
				cargoPorts: prev,
				isCopy,
				incomingCargoPortAttributes: values.attributes,
			});

			return prev.map((cp) => {
				return (cp.id === values.cargoPortId ? {
					...updatedCargoPort,
					...calculatedValues,
					estimatedTimeAllowed:
						values.attributes.estimatedTimeAllowed ??
						convertDaysToInterval(
							calculatedValues.estimatedTimeAllowed,
							updatedCargoPort.laytimeInterval,
						),
					estimatedTimeUsed: values.attributes.estimatedTimeUsed,
					estimatedDemurrage: Math.abs(calculatedValues.estimatedDemurrage ?? 0),
					totalTimeInPort: calculatedValues.totalTimeInPort ?? 0,
					estimatedDespatch: calculatedValues.estimatedDespatch ?? 0,
				} : cp);
			});
		});

		const syncedField = Object.keys(values.attributes)
			.find((key) => key !== 'port') as keyof CargoPortProps | undefined;

		const shouldSyncToVcIn =
			relevantCargo.contractSide === CargoContractSides.VC_OUT &&
			syncedField != null &&
			isSyncedCargoPortField(syncedField);

		if (shouldSyncToVcIn && !isSyncedUpdate) {
			const linkedVcInCargo = localCargoData?.find((c) => (
				c.contractSide === CargoContractSides.VC_IN &&
				(
					c.reletCargoId === relevantCargo.id ||
					relevantCargo.reletCargoId === c.id
				)
			));

			const sameSidePorts = localCargoPortData
				?.filter((cp) => cp.cargoId === relevantCargo.id)
				.sort((a, b) => a.id - b.id) ?? [];

			const linkedSidePorts = localCargoPortData
				?.filter((cp) => cp.cargoId === linkedVcInCargo?.id)
				.sort((a, b) => a.id - b.id) ?? [];

			const sourceIndex = sameSidePorts.findIndex((cp) => cp.id === relevantCargoPort.id);

			const linkedCargoPort = sourceIndex >= 0 ?
				linkedSidePorts[sourceIndex] :
				undefined;

			if (linkedCargoPort != null) {
				onUpdateCargoPort({
					cargoId: linkedCargoPort.cargoId,
					cargoPortId: linkedCargoPort.id,
					attributes: values.attributes,
				}, false, true);
			}
		}

		if (toUpdate.attributes?.port != null) {
			setLocalCargoData((prev) => {
				if (prev == null) {
					return [];
				}

				const updatedCargoPorts = {
					...relevantCargo?.cargoPorts,
					[relevantCargoPort.portAndActionKey]: {
						...relevantCargoPort,
						port: toUpdate.attributes.port,
					} as CargoPortWithEstimatedValues,
				};

				const { action, portId } = splitActionKey(relevantCargoPort.portAndActionKey);
				const isLoading = action === PortActionTypes.LOADING;
				const updatePort = (ports: Port[]) => ports
					.map((p) => (p.id === Number(portId) ? toUpdate.attributes.port : p));

				const updatedCargo = {
					...relevantCargo,
					cargoPorts: updatedCargoPorts,
					loadingPorts: isLoading ?
						updatePort(relevantCargo.loadingPorts) :
						relevantCargo.loadingPorts,
					dischargePorts: isLoading ?
						relevantCargo.dischargePorts :
						updatePort(relevantCargo.dischargePorts),
				};

				return prev.map((c) => (c.id === values.cargoId ? updatedCargo : c));
			});
		}
	};

	const copyCargoPortSettings = (cargoPortId: number) => {
		const relevantCargoPort = localCargoPortData?.find((cp) => cp.id === cargoPortId);

		if (!relevantCargoPort) {
			return;
		}

		const { action } = splitActionKey(relevantCargoPort.portAndActionKey);
		const fn = action === PortActionTypes.LOADING ? isLoadPort : isDischargePort;

		const values = cloneDeep(relevantCargoPort);

		// We don't want all values on the cargoport, as many are calculated fields
		const newValues: Partial<CargoPortProps> = {
			cpQuantity: values.cpQuantity,
			secondaryCpQuantity: values.secondaryCpQuantity,
			blQuantity: values.blQuantity,
			secondaryBlQuantity: values.secondaryBlQuantity,
			blDate: values.blDate,
			calculationMethod: values.calculationMethod,
			demurrage: values.demurrage,
			despatch: values.despatch,
			loadingRate: values.loadingRate,
			estimatedTimeUsed: values.estimatedTimeUsed,
			estimatedTimeAllowed: values.estimatedTimeAllowed,
			idleDays: values.idleDays,
			includeDeadfreight: values.includeDeadfreight,
			laycanFrom: values.laycanFrom,
			laycanTo: values.laycanTo,
			laytimeInterval: values.laytimeInterval,
			linerCostCurrency: values.linerCostCurrency,
			linerCostExchangeRate: values.linerCostExchangeRate,
			linerCostUnit: values.linerCostUnit,
			linerCostPricePerUnit: values.linerCostPricePerUnit,
			stsOpsMotherVessel: values.stsOpsMotherVessel,
			useStsOps: values.useStsOps,
		};

		const cargoPortsToApplyTo = localCargoPortData
			?.filter((cp) => cp.id !== cargoPortId && cp.cargoId === relevantCargoPort.cargoId && fn(cp));

		cargoPortsToApplyTo?.forEach((cp) => {
			onUpdateCargoPort({
				cargoId: cp.cargoId,
				cargoPortId: cp.id,
				attributes: {
					...cp,
					...newValues,
				},
			}, true);
		});
	};

	const showReletView = selectedEstimateId != null ?
		reletViewByEstimateId[selectedEstimateId] ?? false :
		false;

	const hasCargoRelet = useMemo(
		() => (localCargoData ?? []).some(
			cargo => cargo.contractSide === CargoContractSides.VC_IN,
		),
		[localCargoData],
	);

	const handleSetReletView = useCallback(
		(value: boolean | ((prev: boolean) => boolean)) => {
			if (selectedEstimateId == null) {
				return;
			}

			setReletViewByEstimateId(prev => {
				const current = prev[selectedEstimateId] ?? false;

				return {
					...prev,
					[selectedEstimateId]:
						typeof value === 'function' ? value(current) : value,
				};
			});
		},
		[selectedEstimateId],
	);

	return {
		cargos: (localCargoData ?? []).map((c) => ({ ...c, key: c.id })),
		cargoPorts: localCargoPortData,
		cargo: localCargoData?.[0] as GetCargoDetailsResponse,
		hardRefresh,
		onImportCargoMutator,
		onCreateNewCargoMutator,
		onCreateNewCargoReletMutator,
		onUpdateCargo,
		onUpdateCargoPort,
		onDeleteCargoMutator,
		onDeleteCargoReletMutator,
		hasPendingChanges: pendingCargoChanges.length > 0 || pendingCargoPortChanges.length > 0,
		pendingCargoChanges,
		pendingCargoPortChanges,
		setPendingCargoChanges,
		setPendingCargoPortChanges,
		copyCargoPortSettings,
		loading,
		cargoReletConsumptionSet: localCargoData?.[0]?.cargoReletConsumptionSet ?? null,
		showReletView,
		handleSetReletView,
		reletViewByEstimateId,
		setReletViewByEstimateId,
		hasCargoRelet,
	};
};
